/**
 * v2.38 — Generic inline-pill rail wrapper for the Home page.
 *
 * Refactored from HomeDocumentariesRail to be a single reusable component
 * that drives ALL category-pill rails on Home (Documentaries, Family
 * Favourites, Horror Hits, etc.).
 *
 * Props
 * -----
 *   slug             — backend collection slug (e.g. "all-documentaries")
 *   title            — rail header text
 *   initialItems     — items pre-fetched by home-bundle (the "All" view)
 *   pills            — array of { slug, label, count } pills. Supply OR set fetchPillsUrl.
 *   fetchPillsUrl    — optional API path to lazy-fetch pills (e.g. "/docs-categories")
 *   filterParam      — the query param name to send when a pill is selected
 *                      (e.g. "docs_category", "family_filter", "horror_filter")
 *   urlFilterParam   — short query param used in the /collection link
 *                      (e.g. "dc", "ff", "hf")
 *   topInlineCount   — how many pills to show inline before overflowing into a "More ▾"
 *                      dropdown. Default 6.
 *   variant          — optional visual treatment. "horror" applies a dark
 *                      red-tinted backdrop so the rail stands out from neighbours.
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowRight, Star, CalendarDays } from "lucide-react";
import MovieCarousel from "./MovieCarousel";
import { api } from "../lib/api";

// v2.58 — Module-level in-memory cache so flipping between pills/sort is
// instant after the first fetch. Cache is per (slug, pillSlug, sort).
// Cleared on page refresh — fresh-enough that staleness isn't an issue.
const _PILL_CACHE = new Map();
const _pillKey = (slug, pillSlug, sort) => `${slug}::${pillSlug}::${sort}`;

// Compact sort toggle — mirrors the NIT/OTT Trending/Top Rated toggle
function CollectionSortTabs({ value, onChange, testidPrefix, variant }) {
  const base = "px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide transition-all";
  const activeHorror  = "bg-rose-500/25 text-rose-200";
  const activeNormal  = "bg-amber-500/25 text-amber-200";
  const inactive      = "text-white/50 hover:text-white/80";
  const isHorror = variant === "horror";
  return (
    <div
      className="inline-flex items-center bg-white/[0.04] border border-white/10 rounded-full p-0.5 gap-0.5 shrink-0"
      data-testid={`${testidPrefix}-sort-tabs`}
    >
      <button
        type="button"
        onClick={() => onChange("score")}
        data-testid={`${testidPrefix}-sort-score`}
        className={`${base} inline-flex items-center gap-1 ${value === "score" ? (isHorror ? activeHorror : activeNormal) : inactive}`}
      >
        <Star className="w-2.5 h-2.5" />
        <span>TOP</span>
      </button>
      <button
        type="button"
        onClick={() => onChange("newest")}
        data-testid={`${testidPrefix}-sort-newest`}
        className={`${base} inline-flex items-center gap-1 ${value === "newest" ? (isHorror ? activeHorror : activeNormal) : inactive}`}
      >
        <CalendarDays className="w-2.5 h-2.5" />
        <span>LATEST</span>
      </button>
    </div>
  );
}

export default function HomeCategoryRail({
  slug,
  title,
  initialItems = [],
  pills: pillsProp,
  fetchPillsUrl,
  filterParam = "docs_category",
  urlFilterParam = "dc",
  topInlineCount = 6,
  variant,
  testid,
  isNew = false,
  totalCount = null,
}) {
  const [pills, setPills] = useState(pillsProp || []);
  const [active, setActive] = useState("all");
  const [sort, setSort] = useState("score");
  const [items, setItems] = useState(initialItems);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const rowTestId = testid || `row-${slug}`;

  // Lazy-fetch pills if a URL is provided (e.g. /docs-categories).
  useEffect(() => {
    if (pillsProp && pillsProp.length) { setPills(pillsProp); return; }
    if (!fetchPillsUrl) return;
    let alive = true;
    api.get(fetchPillsUrl).then((r) => {
      if (alive) setPills(r.data?.pills || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [pillsProp, fetchPillsUrl]);

  // Re-sync items on parent refresh of the "All" view (only when sort=score, active=all).
  useEffect(() => {
    if (active === "all" && sort === "score") setItems(initialItems);
  }, [initialItems, active, sort]);

  // Outside-click closes dropdown.
  useEffect(() => {
    if (!dropdownOpen) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [dropdownOpen]);

  const fetchItems = async (pillSlug, sortVal) => {
    // "All" + "score" = initial bundle data, no fetch needed
    if (pillSlug === "all" && sortVal === "score") {
      setItems(initialItems);
      return;
    }
    const cacheKey = _pillKey(slug, pillSlug, sortVal);
    const cached = _PILL_CACHE.get(cacheKey);
    if (cached) {
      setItems(cached);
      // Background revalidate
      api.get(`/collections/${slug}`, {
        params: { [filterParam]: pillSlug === "all" ? undefined : pillSlug, sort: sortVal, page: 1 },
      }).then((r) => {
        const fresh = r.data?.results || [];
        if (fresh.length) { _PILL_CACHE.set(cacheKey, fresh); setItems(fresh); }
      }).catch(() => {});
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/collections/${slug}`, {
        params: { [filterParam]: pillSlug === "all" ? undefined : pillSlug, sort: sortVal, page: 1 },
      });
      const fresh = r.data?.results || [];
      setItems(fresh);
      if (fresh.length) _PILL_CACHE.set(cacheKey, fresh);
    } catch { /* keep previous items */ }
    finally { setLoading(false); }
  };

  const onPick = (pillSlug) => {
    setActive(pillSlug);
    setDropdownOpen(false);
    fetchItems(pillSlug, sort);
  };

  const onSortChange = (newSort) => {
    setSort(newSort);
    fetchItems(active, newSort);
  };

  const viewAllHref =
    active === "all"
      ? `/collection/${slug}`
      : `/collection/${slug}?${urlFilterParam}=${active}`;

  const topPills = pills.slice(0, topInlineCount);
  const morePills = pills.slice(topInlineCount);
  const activePill = pills.find((p) => p.slug === active);

  const variantBgClass =
    variant === "horror"
      ? "relative px-6 lg:px-10 py-7 fm-fade bg-gradient-to-br from-rose-950/30 via-zinc-950/40 to-black/30 border-y border-rose-900/20"
      : "relative px-6 lg:px-10 py-6 fm-fade";

  return (
    <section className={variantBgClass} data-testid={`${rowTestId}-wrap`}>
      <div className="max-w-[1440px] mx-auto mb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Title + NEW badge */}
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2.5">
            {title}
            {isNew && (
              <span
                data-testid={`${rowTestId}-new-badge`}
                className="inline-flex items-center text-[10px] font-extrabold tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-rose-500 text-zinc-900 shadow-md animate-pulse"
                title="Recently added rail"
              >
                New
              </span>
            )}
          </h2>

          {/* Right controls: sort toggle + view all */}
          <div className="flex items-center gap-3 flex-wrap">
            <CollectionSortTabs
              value={sort}
              onChange={onSortChange}
              testidPrefix={rowTestId}
              variant={variant}
            />
            <Link
              to={viewAllHref}
              data-testid={`${rowTestId}-viewall`}
              className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-400 hover:text-white transition-colors whitespace-nowrap"
            >
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {pills.length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`${rowTestId}-pills`}>
            {topPills.map((pill) => (
              <button
                key={pill.slug}
                onClick={() => onPick(pill.slug)}
                data-testid={`${rowTestId}-pill-${pill.slug}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wide transition border ${
                  active === pill.slug
                    ? variant === "horror"
                      ? "bg-rose-500/30 text-rose-100 border-rose-400/60"
                      : "bg-orange-500/25 text-orange-100 border-orange-400/50"
                    : "bg-white/[0.03] text-white/65 border-white/10 hover:bg-white/[0.08] hover:text-white/90"
                }`}
              >
                {pill.label}
                {typeof pill.count === "number" && <span className="opacity-60 ml-1">· {pill.count}</span>}
              </button>
            ))}
            {morePills.length > 0 && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((s) => !s)}
                  data-testid={`${rowTestId}-more-toggle`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wide transition border inline-flex items-center gap-1 ${
                    activePill && morePills.includes(activePill)
                      ? variant === "horror"
                        ? "bg-rose-500/30 text-rose-100 border-rose-400/60"
                        : "bg-orange-500/25 text-orange-100 border-orange-400/50"
                      : "bg-white/[0.03] text-white/65 border-white/10 hover:bg-white/[0.08] hover:text-white/90"
                  }`}
                >
                  {activePill && morePills.includes(activePill) ? activePill.label : "More"}
                  <ChevronDown className={`w-3 h-3 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {dropdownOpen && (
                  <div
                    className="absolute left-0 top-full mt-2 z-20 w-[240px] max-h-[60vh] overflow-y-auto rounded-xl border border-white/15 bg-[#0E0E0E]/95 backdrop-blur-md shadow-2xl py-1"
                    data-testid={`${rowTestId}-more-menu`}
                  >
                    {morePills.map((pill) => (
                      <button
                        key={pill.slug}
                        onClick={() => onPick(pill.slug)}
                        data-testid={`${rowTestId}-pill-${pill.slug}`}
                        className={`w-full text-left px-4 py-2 text-xs flex items-center justify-between transition ${
                          active === pill.slug
                            ? "bg-orange-500/15 text-orange-100"
                            : "text-white/75 hover:bg-white/[0.06] hover:text-white"
                        }`}
                      >
                        <span>{pill.label}</span>
                        {typeof pill.count === "number" && (
                          <span className="text-[10px] text-white/50">
                            {pill.count} <ChevronRight className="inline w-3 h-3" />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div className="max-w-[1440px] mx-auto pb-2 -mt-1 text-[11px] text-neutral-500" data-testid={`${rowTestId}-loading`}>
          Loading {activePill?.label?.toLowerCase() || "selection"}…
        </div>
      )}

          {/* v3.31 — Pro "See all X titles" free-tier tile and Pro "See More"
              end-card work correctly: pass collectionSlug + totalCount so
              MovieCarousel knows the real pool size and where to link. */}
      <MovieCarousel
        title=""
        items={items}
        testid={rowTestId}
        showPlatform
        disableShuffle
        collectionSlug={slug}
        totalCount={totalCount}
      />
    </section>
  );
}
